/**
 * @copyright Copyright 2023-2023 Epic Systems Corporation
 * @file button to trigger an email to the vendor
 * @module Epic.AppOrchard.Showroom.ShowroomContactButton
 */

import { ButtonProps } from "@chakra-ui/react";
import { ContactButton } from "ao/components/Core";
import { contactAuthor } from "ao/data";
import React, { FunctionComponent, memo } from "react";
import { AiOutlineMail } from "react-icons/ai";

interface IProps {
	appId: number;
	appName: string;
	orgName: string;
	iconColor: string;
	buttonProps?: ButtonProps;
}

export const ShowroomContactButton: FunctionComponent<IProps> = memo((props) => {
	const { appId, appName, orgName, iconColor, buttonProps } = props;

	const computedButtonProps: ButtonProps = {
		fontWeight: buttonProps?.fontWeight || "normal",
		px: buttonProps?.px || buttonProps?.paddingX || 2,
		variant: buttonProps?.variant || "card",
		iconSpacing: buttonProps?.iconSpacing || "3px",
		title: buttonProps?.title || `Let ${orgName} know you're interested in ${appName}`,
		...buttonProps,
	};

	return (
		<ContactButton
			{...computedButtonProps}
			contactApi={contactAuthor}
			leftIcon={<AiOutlineMail color={iconColor} />}
			apiArgs={[appId]}
			buttonCaption="Contact"
			buttonNotContactedHelptext="Send an email to the vendor indicating your interest and inviting them to start a conversation with you."
			successMessage="An email was sent to the vendor."
			errorMessage="Error contacting the vendor. Please try again later or contact your Epic representative if this continues to happen."
			confirmModalTitle={`Contact ${orgName ?? appName}`}
			confirmModalPrompt={`This will send an email to ${
				orgName ?? appName
			} indicating your interest and inviting them to start a conversation with you. Are you sure you want to continue?`}
		/>
	);
});
